import { Filter, Table } from '@otp/ui';
import { getTableConfig } from './restaurants-unassigned-list-config';
import {
  Actions,
  AppArea,
  StoreFilterInput,
  StoresUserDetailsDocument,
  StoresUserDetailsQuery,
  Subjects,
  User,
} from 'src/graphql/generated/graphql';
import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { t } from 'i18next';
import { useCan } from 'src/utils/can';

type RestaurantUnassignnedListProps = {
  user: Partial<User>;
};

const PAGINATION_LIMIT = 5;

export const RestaurantUnassignedList = ({
  user,
}: RestaurantUnassignnedListProps) => {
  const { can } = useCan();
  const canAccess = can({
    action: Actions.Assign,
    subject: Subjects.Stores,
    appArea: AppArea.Common,
  });
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const onFilterChange = (filter: StoreFilterInput) => {
    setPage(0);
    setSearch(filter.search || '');
  };

  const { loading, data } = useQuery<StoresUserDetailsQuery>(
    StoresUserDetailsDocument,
    {
      variables: {
        userId: user.id,
        filter: {
          search: search,
          organizationId: user.organizationId,
          notAssignedToUserId: user.id,
        },
        pagination: {
          limit: PAGINATION_LIMIT,
          offset: page * PAGINATION_LIMIT,
        },
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-only',
      skip: !canAccess,
    },
  );

  const totalCount = data?.stores?.pagination?.totalCount || 0;
  const stores = data?.stores?.records || [];

  const tableConfig = getTableConfig({ stores: stores, user: user }, { t });

  const title = `${t(
    'user-details.stores-unassigned',
    'Stores unassigned',
  )} (${totalCount})`;

  return (
    canAccess && (
      <>
        <Filter
          className="!bg-white !p-0 !pt-2"
          searchInputProps={{
            label: t('user-details.search', 'Search'),
          }}
          onSubmit={onFilterChange}
        />
        <Table
          title={title}
          data={stores}
          columns={tableConfig}
          options={{
            count: totalCount,
            rowsPerPage: PAGINATION_LIMIT,
            onChangePage: setPage,
            pagination: true,
          }}
          loading={loading}
        />
      </>
    )
  );
};
